import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useYearContext } from '../contexts/YearContext';
import { apiRequest } from '../lib/queryClient';
import { Button } from './ui/button';

/** Lock / unlock the selected reporting year for inventory finalize. */
export function YearLockControls() {
  const { currentCompany, selectedYear } = useYearContext();
  const queryClient = useQueryClient();
  const companyId = currentCompany?.id;
  const year = selectedYear;

  const { data: lockStatus, isLoading } = useQuery({
    queryKey: ['/api/company/year-lock', companyId, year],
    queryFn: async () => {
      const res = await apiRequest(
        'GET',
        `/api/company/${companyId}/year-lock?reporting_year=${year}`
      );
      return res.json();
    },
    enabled: !!companyId && !!year,
  });

  const lockMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('POST', `/api/company/${companyId}/year-lock`, {
        company_id: companyId,
        reporting_year: year,
        reason: 'Inventory finalized',
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/company/year-lock', companyId, year] });
    },
    onError: (err: Error) => alert(err.message),
  });

  const unlockMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        'DELETE',
        `/api/company/${companyId}/year-lock?reporting_year=${year}`
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/company/year-lock', companyId, year] });
    },
    onError: (err: Error) => alert(err.message),
  });

  if (!companyId || !year) return null;

  const isLocked = !!lockStatus?.is_locked;

  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border ${
      isLocked ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'
    }`}>
      <div className="flex-1 text-sm">
        <span className="font-medium text-gray-800">Reporting year {year}: </span>
        {isLoading ? (
          <span className="text-gray-500">Checking lock…</span>
        ) : isLocked ? (
          <span className="text-amber-800">Locked — edits blocked</span>
        ) : (
          <span className="text-gray-600">Open for data entry</span>
        )}
      </div>
      {isLocked ? (
        <Button
          size="sm"
          variant="outline"
          disabled={unlockMutation.isPending}
          onClick={() => unlockMutation.mutate()}
        >
          Unlock
        </Button>
      ) : (
        <Button
          size="sm"
          disabled={lockMutation.isPending}
          onClick={() => {
            if (confirm(`Lock ${year}? No further emission edits will be allowed until unlocked.`)) {
              lockMutation.mutate();
            }
          }}
        >
          Lock year
        </Button>
      )}
    </div>
  );
}
